import { t } from './i18n';
import type { Network } from './network';
import { checkStops } from './stop-checks';
import type { LineTimetableSummary } from './timetables';
import { checkZone, metres } from './zone-check';

export type Severity = 'problem' | 'check';
export type Area = 'lines' | 'directions' | 'stations' | 'timetables';

export interface Issue {
  area: Area;
  severity: Severity;
  /** What it is about, e.g. "1 · Linja 1" or a station name. */
  subject: string;
  text: string;
  /** Where it is fixed. */
  link: string;
}

/** Stations closer than this to each other are probably the same stop. */
const DUPLICATE_M = 20;
/** Two stations with the same name closer than this are probably the same stop. */
const SAME_NAME_M = 150;

/**
 * Every data check in one list: what the line, direction, station and timetable pages each warn
 * about, plus the things only visible across all of them (unused stations, duplicates).
 */
export function dataHealth(network: Network, timetables: LineTimetableSummary[] = []): Issue[] {
  const issues: Issue[] = [];
  const stations = new Map(network.stations.map((s) => [s._id, s]));
  const zoneOf = new Map<string, Network['zones'][number]>();
  for (const z of network.zones) {
    if (z.on_line_going) zoneOf.set(z.on_line_going, z);
    if (z.on_line_return) zoneOf.set(z.on_line_return, z);
  }
  const zoneName = (z: Network['zones'][number]) => `${z.bus_sign} · ${z.name}`;

  // Lines: both directions chosen, fences where they belong.
  for (const z of network.zones) {
    const link = `/lines/${z._id}`;
    if (!z.on_line_going || !z.on_line_return) {
      issues.push({
        area: 'lines',
        severity: z.is_active ? 'problem' : 'check',
        subject: zoneName(z),
        text: !z.on_line_going ? t('Has no going direction.') : t('Has no return direction.'),
        link,
      });
      continue;
    }
    const c = checkZone(z, network);
    if (c.going && !c.going.ok)
      issues.push({
        area: 'lines',
        severity: 'problem',
        subject: zoneName(z),
        text: c.going.swapped
          ? t('The fences look swapped.')
          : t('The going fence is {m} m from the start of the going direction.', {
              m: c.going.metresToOwn ?? '?',
            }),
        link,
      });
    else if (c.return && !c.return.ok)
      issues.push({
        area: 'lines',
        severity: 'problem',
        subject: zoneName(z),
        text: t('The return fence is {m} m from the start of the return direction.', {
          m: c.return.metresToOwn ?? '?',
        }),
        link,
      });
    if (c.returnMatches === false)
      issues.push({
        area: 'lines',
        severity: 'check',
        subject: zoneName(z),
        text: t('The return direction does not start where the going direction ends.'),
        link,
      });
  }

  // Directions: route, stops on it and in order, used by a line.
  for (const l of network.lines) {
    const link = `/directions/${l._id}`;
    const subject = l.name;
    if (!zoneOf.has(l._id))
      issues.push({ area: 'directions', severity: 'check', subject, text: t('Not used by any line.'), link });
    if (!l.geometry?.coordinates?.length) {
      issues.push({ area: 'directions', severity: 'problem', subject, text: t('Has no route drawn.'), link });
      continue;
    }
    if (l.ordered_stations.length < 2) {
      issues.push({
        area: 'directions',
        severity: 'problem',
        subject,
        text: t('Has fewer than two stops.'),
        link,
      });
      continue;
    }
    const stops = checkStops(l.geometry.coordinates, l.ordered_stations, stations);
    const far = stops.filter((s) => s.far);
    const order = stops.filter((s) => s.outOfOrder);
    if (far.length)
      issues.push({
        area: 'directions',
        severity: 'problem',
        subject,
        text: t('Stops far from the route ({n}): {names}', {
          n: far.length,
          names: far.map((s) => `${s.name} (${s.distance} m)`).join(', '),
        }),
        link,
      });
    if (order.length)
      issues.push({
        area: 'directions',
        severity: 'problem',
        subject,
        text: t('Stops out of order ({n}): {names}', {
          n: order.length,
          names: order.map((s) => s.name).join(', '),
        }),
        link,
      });
  }

  // Stations: on a direction, not duplicated.
  const used = new Set(network.lines.flatMap((l) => l.ordered_stations));
  for (const s of network.stations)
    if (!used.has(s._id))
      issues.push({
        area: 'stations',
        severity: 'check',
        subject: s.name,
        text: t('Not on any direction (not shown with a line in the app).'),
        link: `/stations/${s._id}`,
      });
  const list = network.stations;
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++) {
      const [a, b] = [list[i]!, list[j]!];
      const d = metres(
        a.geometry.coordinates as [number, number],
        b.geometry.coordinates as [number, number],
      );
      const sameName = a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
      // Stops facing each other across a road serve different directions: not duplicates.
      if (a.lines.length && b.lines.length && !a.lines.some((l) => b.lines.includes(l))) continue;
      if (d < DUPLICATE_M || (sameName && d < SAME_NAME_M))
        issues.push({
          area: 'stations',
          severity: 'check',
          subject: `${a.name} / ${b.name}`,
          text: t('Two stations {m} m apart: probably the same stop.', { m: Math.round(d) }),
          link: `/stations/${a._id}`,
        });
    }

  // Timetables: every direction of a line in service has times at all its stops.
  for (const tt of timetables) {
    const zone = zoneOf.get(tt._id);
    if (!zone?.is_active || !tt.stations) continue;
    if (tt.with_times < tt.stations)
      issues.push({
        area: 'timetables',
        severity: tt.with_times ? 'problem' : 'check',
        subject: tt.name,
        text: tt.with_times
          ? t('Times at {a} of {b} stops.', { a: tt.with_times, b: tt.stations })
          : t('No timetable.'),
        link: `/schedules/directions/${tt._id}`,
      });
  }
  return issues;
}
